import Icon, { QuestionCircleOutlined, SettingOutlined, CarOutlined } from "@ant-design/icons";
import { Dropdown, Image, Tooltip, Popover, Menu } from "antd";
import { Link, navigate } from "gatsby";
import React, { useContext, useState } from "react";
import { UserContext } from "../../amplify/authenticator-provider";
import { getCognitoRoles } from "../../hooks/utils";
import AnalyticsIcon from "../../icons/js/AnalyticsIcon";
import ConversationIcon from "../../icons/js/ConversationIcon";
import EngagementsIcon from "../../icons/js/EngagementsIcon";
import PersonalizedIcon from "../../icons/js/PersonalizedIcon";
import ProspectsIcon from "../../icons/js/ProspectsIcon";
import ReviewsIcon from "../../icons/js/ReviewsIcon";
import { openDialog } from "../../library/helpers";
import AntdIconV4 from "../../utils/antdIcon";
import ReportIcon from "../../icons/svg/report-icon.svg";
import { getRoutePath, ROUTES } from "../../Routes";
import { isAdminRoute } from "../../utils/ReportUtils";

const NavigationContainer = props => {
  const { setContentStyle, accessPayload, activeTab } = props;

  const { user } = useContext(UserContext);

  const activeSlug = window.location.pathname;

  const shouldHideSideNav = isAdminRoute();

  const NAVIGATION_OPTIONS = [
    {
      id: 1,
      label: "Admin",
      link: "/admin-dashboard",
      // icon: CogIcon,
      icon: <SettingOutlined />,
      type: "setting",
      isAdminSetting: getCognitoRoles(accessPayload)[0] === "ADMIN",
    },
    {
      id: 2,
      label: "Analytics",
      link: "/analytics/kpis",
      // icon: AnalyticsIcon,
      icon: <AnalyticsIcon />,
      type: "pieChart",
      isAdminSetting:
        getCognitoRoles(accessPayload)[0] !== "ADMIN" && user?.role?.canViewAutoAnalytics,
    },
    {
      id: 3,
      label: "Prospects",
      link: user?.role?.canViewProspects ? "/prospect" : "/no-permission",
      // icon: ProspectsIcon,
      icon: <ProspectsIcon />,
      type: "funnelPlot",
      isAdminSetting: getCognitoRoles(accessPayload)[0] !== "ADMIN" && user?.role?.canViewProspects,
    },
    {
      id: 4,
      label: "Engagements",
      link: "/engagements/dashboard",
      urlPart: "engagements",
      // icon: EngagementIcon,
      icon: <EngagementsIcon />,
      type: "mail",
      isAdminSetting:
        getCognitoRoles(accessPayload)[0] !== "ADMIN" && user?.role?.canViewEngagements,
    },
    {
      id: 5,
      label: "Conversations",
      link: "/conversation",
      // icon: ConversationsIcon,
      icon: <ConversationIcon />,
      type: "message",
      isAdminSetting:
        getCognitoRoles(accessPayload)[0] !== "ADMIN" &&
        (user?.role?.canViewClm || user?.role?.canViewProspects),
    },
    {
      id: 6,
      label: "Reviews",
      link: "/reviews",
      // icon: StarIcon,
      icon: <ReviewsIcon />,
      type: "star",
      isAdminSetting:
        getCognitoRoles(accessPayload)[0] !== "ADMIN" && user?.role?.canViewEngagements,
    },
    // {
    //   id: 9,
    //   label: "Email Analytics",
    //   link: ROUTES.emailAnalytics,
    //   icon: <Image preview={false} src={ReportIcon} alt="Email Analytics Icon" />,
    //   type: "emailAnalytics",
    //   isAdminSetting:
    //     getCognitoRoles(accessPayload)[0] !== "ADMIN" && user?.role?.canViewEngagements,
    // },
    {
      id: 8,
      label: "Reports",
      link: ROUTES.reports,
      icon: <Image preview={false} src={ReportIcon} alt="Reports Icon" />,
      type: "reports",
      isAdminSetting:
        getCognitoRoles(accessPayload)[0] !== "ADMIN" && user?.role?.canViewEngagements,
    },
    {
      id: 9,
      label: "Inventory",
      link: "/inventory",
      icon: <CarOutlined style={{ color: "gray" }} />,
      type: "inventory",
      isAdminSetting:
        getCognitoRoles(accessPayload)[0] !== "ADMIN" && user?.role?.canViewEngagements,
    },
    {
      id: 7,
      label: "Personalized",
      link: "/personalized-ads",
      // icon: ExportIcon,
      icon: <PersonalizedIcon />,
      type: "download",
      isAdminSetting: getCognitoRoles(accessPayload)[0] !== "ADMIN" && user?.role?.canViewAdExport,
    },
  ];

  const [isNavInFullWidth, setIsNavInFullWidth] = useState(false);

  const handleNavigationWidth = () => {
    setIsNavInFullWidth(prev => !prev);
    setContentStyle(prev => !prev);
  };

  const userCurrentLocation = sessionStorage.getItem("currentLocationId") || "";
  const locationIds = userCurrentLocation?.split(",").filter(id => id.trim());

  // Popover content for Reports
  const reportsPopoverContent = (
    <Menu
      style={{ borderInlineEnd: "none" }}
      items={[
        {
          key: "email-analytics-reports",
          label: (
            <div
              onClick={() =>
                navigate(
                  locationIds?.length === 1
                    ? getRoutePath(ROUTES.emailLocationAnalytics, locationIds[0])
                    : ROUTES.emailAnalytics
                )
              }
            >
              Email Reports
            </div>
          ),
        },
        {
          key: "call-reports",
          label: (
            <div
              onClick={() =>
                navigate(
                  locationIds?.length === 1
                    ? getRoutePath(ROUTES.locationReports, locationIds[0])
                    : ROUTES.reports
                )
              }
            >
              Call Reports
            </div>
          ),
        },
        {
          key: "otto-analytics",
          label: <div onClick={() => navigate(ROUTES.ottoAnalytics)}>Ottolytics</div>,
        },
        {
          key: "daily-checkout-report",
          label: <div onClick={() => navigate(ROUTES.dailyCheckoutReport)}>Checkout Report</div>,
        },
      ]}
    />
  );

  // mobile navigation options
  const items = NAVIGATION_OPTIONS.map(nav => {
    if (getCognitoRoles(accessPayload)[0] !== "ADMIN") {
      if (nav.label !== "Admin") {
        return {
          key: nav.id,
          label: (
            <div
              className="flex gap-2 py-1 px-2 font-medium text-[17px]"
              onClick={() => navigate(nav.link)}
            >
              {/* <Icon className="text-xl leading-none" component={nav.icon} /> */}
              {nav.icon}
              <div>{nav.label}</div>
            </div>
          ),
        };
      }
    } else {
      return {
        key: nav.id,
        label: (
          <div
            className="flex gap-2 py-1 px-2 font-medium text-[17px]"
            onClick={() => navigate(nav.link)}
          >
            <Icon className="text-xl leading-none text-blue-500" component={nav.icon} />
            <div>{nav.label}</div>
          </div>
        ),
      };
    }
  });

  return (
    <>
      {!shouldHideSideNav && (
        <div className="fixed left-0 z-10 bg-white hidden md:block h-[calc(100vh-3rem)]">
          <div
            className={`${
              isNavInFullWidth ? "w-40" : "w-[57px]"
            } flex flex-col justify-between text-center h-full transition-all duration-75`}
          >
            <div className="flex flex-col">
              {NAVIGATION_OPTIONS.map(link => {
                if (link.label === "Reports") {
                  // Enhancement: Highlight Reports icon for both call and email analytics routes
                  const isReportsActive =
                    activeSlug.includes(
                      link.link === "/engagements/dashboard" ? link.urlPart : link.link
                    ) ||
                    // New: highlight if url contains 'email-analytics' or 'report'
                    /email-analytics|report|otto-analytics|abandoned-calls/i.test(activeSlug);
                  return (
                    <Popover
                      key={link.id}
                      content={reportsPopoverContent}
                      trigger={["hover", "click"]}
                      placement="right"
                    >
                      <div
                        className={`py-3 no-underline w-full cursor-pointer ${
                          isNavInFullWidth ? "flex items-center px-3" : ""
                        } transition-all duration-75 ${
                          isReportsActive ? "primaryLightBgTheme" : ""
                        } hover:bg-blue-200 ${!link.isAdminSetting ? "hidden" : ""}`}
                      >
                        {link.icon}
                        {isNavInFullWidth && <p className="m-0 pl-2 text-sm">{link.label}</p>}
                      </div>
                    </Popover>
                  );
                }
                if (getCognitoRoles(accessPayload)[0] !== "ADMIN") {
                  if (link.label !== "Admin") {
                    return (
                      <Tooltip title={link.label} key={link.id} placement="right">
                        <div
                          className={`py-3 no-underline w-full cursor-pointer ${
                            isNavInFullWidth ? "flex items-center px-3" : ""
                          } transition-all duration-75 ${
                            activeSlug.includes(
                              link.link === "/engagements/dashboard" ? link.urlPart : link.link
                            )
                              ? "primaryLightBgTheme"
                              : ""
                          } hover:bg-blue-200 ${!link.isAdminSetting ? "hidden" : ""}`}
                          onClick={() => navigate(link.link)}
                        >
                          {link.icon}
                          {isNavInFullWidth && <p className="m-0 pl-2 text-sm">{link.label}</p>}
                        </div>
                      </Tooltip>
                    );
                  }
                } else {
                  return (
                    <Tooltip title={link.label} key={link.id} placement="right">
                      <Link
                        className={`py-3 no-underline w-full cursor-pointer ${
                          isNavInFullWidth ? "flex items-center px-3" : ""
                        } transition-all duration-75 ${
                          activeSlug.includes(
                            link.link === "/engagements/dashboard" ? link.urlPart : link.link
                          )
                            ? "primaryLightBgTheme"
                            : ""
                        } hover:bg-blue-200 ${!link.isAdminSetting ? "hidden" : ""}`}
                        to={link.link}
                      >
                        {link.icon}
                        {isNavInFullWidth && <p className="m-0 pl-2 text-sm">{link.label}</p>}
                      </Link>
                    </Tooltip>
                  );
                }
              })}
            </div>
            <div>
              <Tooltip placement="right" title="Support">
                <div
                  className={`py-3 no-underline w-full cursor-pointer ${
                    isNavInFullWidth ? "flex items-center px-3" : ""
                  } transition-all duration-75`}
                  onClick={() =>
                    openDialog("supportModal", {
                      dialogHeader: "Need to report an issue? Have a question?",
                    })
                  }
                >
                  {/* <Avatar className="sideBarBG" title={link.label} shape="square">
                      <Icon className="text-xl leading-none" component={link.icon} />
                    </Avatar> */}
                  <QuestionCircleOutlined className="h-5 w-5 text-grey-500" />
                  {isNavInFullWidth && <p className="m-0 pl-2 text-sm">Support</p>}
                </div>
              </Tooltip>
              <AntdIconV4
                onClick={() => handleNavigationWidth()}
                className="mb-5 h-12 flex justify-center items-center hover:bg-blue-300 hover:font-bold"
                type={isNavInFullWidth ? "LeftOutlined" : "RightOutlined"}
              />
            </div>
          </div>
        </div>
      )}

      <Dropdown
        menu={{ items }}
        className="cursor-pointer w-full text-center text-sm py-1 text-white primaryBgTheme md:hidden"
        placement="bottom"
        trigger="click"
      >
        <div>{activeTab}</div>
      </Dropdown>
    </>
  );
};

export default NavigationContainer;
